import { useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import SegmentedTabs from '../../../reusable/SegmentedTabs/SegmentedTabs';
import Button from '../../../reusable/Button/Button';
import Skeleton from '../../../reusable/Skeleton/Skeleton';
import { useTopbarSlot } from '../../../layout/TopbarSlot';
import {
  useGetCompetitionLeaderboardQuery,
  useGetCompetitionRulesQuery,
} from '../../../store/api/competitionsApi';
import useCountdown from '../useCountdown';
import { statusOf } from '../competition';
import LeaderboardTable from '../LeaderboardTable';
import ParticipateCard from './ParticipateCard';
import { usd } from '../../../utilities/format';
import { t } from '../../../i18n/i18n';
import './CompetitionDetail.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });
const pad = (n) => String(n).padStart(2, '0');

const UNITS = [
  ['days', 'd'],
  ['hours', 'h'],
  ['minutes', 'm'],
  ['seconds', 's'],
];

const STATUS_META = {
  live: { label: 'Live now', tone: 'success' },
  upcoming: { label: 'Upcoming', tone: 'info' },
  ended: { label: 'Ended', tone: 'neutral' },
};

const ordinal = (n) => {
  const suffix = ['{{n}}th', '{{n}}st', '{{n}}nd', '{{n}}rd'];
  const v = n % 100;
  return t(suffix[(v - 20) % 10] ?? suffix[v] ?? suffix[0], { n });
};

/** "Rule 4: Max Daily Drawdown: 5%" — the prefix is the list marker's job. */
const stripRulePrefix = (rule) => String(rule).replace(/^\s*Rule\s*\d+\s*[:.-]\s*/i, '');

const fmtDate = (value, opts) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : format(d, opts);
};

export default function CompetitionDetail() {
  const { competitionId } = useParams();

  // ---- Overview / Competitions tablist — lives in the top bar, 1:9020.
  // Detail is a drill-down from the Competitions list, so it keeps
  // "Competitions" active rather than leaving the top bar empty.
  const location = useLocation();
  const navigate = useNavigate();
  const activeView = location.pathname === '/competition' ? 'Overview' : 'Competitions';

  useTopbarSlot(
    useCallback(
      () => (
        <SegmentedTabs
          size="lg"
          radius="tab-track"
          className="competition-toggle"
          options={['Overview', 'Competitions'].map((v) => ({ value: v, label: t(v) }))}
          value={activeView}
          onChange={(next) => navigate(next === 'Competitions' ? '/competition/all' : '/competition')}
        />
      ),
      [activeView, navigate]
    ),
    [activeView]
  );

  const { data, isLoading, isError } = useGetCompetitionLeaderboardQuery({
    competition_id: competitionId,
  });

  // Its own call, and deliberately not awaited with the one above: the page is
  // readable without it, so the two cards below fall back to what the detail
  // payload carries until it lands.
  const { data: rulesData, isLoading: rulesLoading } = useGetCompetitionRulesQuery();

  const detail = data?.competition ?? null;
  // Past its end it is ended, whatever the record still says — so a finished
  // competition stops offering the entry form (N64).
  const status = statusOf(detail);
  const statusMeta = STATUS_META[status];
  const upcoming = status === 'upcoming';

  // `starts_at` / `ends_at` are the competition's own instants, sent with an
  // offset — so the clock is right wherever the viewer is, without the page
  // trusting their device for anything but "now". (The payload's earlier
  // `countdown.total_seconds` is gone; these replaced it.)
  const target = Date.parse((upcoming ? detail?.starts_at : detail?.ends_at) ?? '');
  const countdown = useCountdown(Number.isFinite(target) ? target : 0);

  // Straight from the payload — `LeaderboardTable`'s columns are keyed on its
  // field names. `leaderboard` is the paged board: `results` are the rows and
  // `meta` is the page it came from.
  const board = data?.leaderboard;
  const rows = useMemo(() => board?.results ?? [], [board]);
  const currentUser = data?.user ?? null;

  // Whether the trader is already in. `competition.user` is the membership
  // block the list and detail payloads carry; this endpoint answers with a
  // board instead, where having a row of your own IS the membership. Either
  // one settles it, so whichever the payload brings is what decides.
  const joined = Boolean(detail?.user?.joined ?? currentUser);

  // Entering is only a question while there is something left to enter. An
  // ended competition drops the card entirely rather than showing a form
  // that could only be refused.
  const canEnter = status !== 'ended';

  const tags = [
    detail?.open_to,
    detail?.min_trading_days
      ? t('Minimum {{count}} trading days', { count: detail.min_trading_days })
      : null,
    detail?.trader_count != null
      ? t('{{count}} entered', { count: detail.trader_count.toLocaleString('en-US') })
      : null,
  ].filter(Boolean);

  // `awards` IS the prize table, and it arrives already collapsed into rank
  // ranges — `rank_from`/`rank_to`, one row standing for all 40 of ranks
  // 31–70 — which is the shape the card is drawn for. The detail payload's
  // First_prize / second_prize / Third_prize are not a fallback for it — they
  // disagree with it — so an empty list means no distribution is published,
  // not "use those".
  const prizes = rulesData?.awards ?? [];

  const ruleText = rulesData?.rules ?? [];

  // The stand-in until the rules land — the entry conditions the detail payload
  // does carry, so the card is never empty.
  const ruleFacts = [
    { label: 'Challenge', value: detail?.challenge },
    { label: 'Account size', value: detail?.account_size_label },
    { label: 'Open to', value: detail?.open_to },
    {
      label: 'Minimum trading days',
      value:
        detail?.min_trading_days != null
          ? t('{{count}} days', { count: detail.min_trading_days })
          : null,
    },
  ].filter((rule) => rule.value);

  const crumb = (
    <nav className="cd-crumb" aria-label={t('Breadcrumb')}>
      <Link to="/competition/all" className="cd-crumb__link">
        {t('Competition')}
      </Link>
      <span className="cd-crumb__sep">/</span>
      <span className="cd-crumb__current">{detail?.competition_name || t('Competition')}</span>
    </nav>
  );

  if (isLoading) {
    return (
      <div className="competition-detail">
        {crumb}
        <section className="cdh">
          <div className="cdh__main">
            <Skeleton w={78} h={20} radius={999} light />
            <Skeleton w="54%" h={30} light />
            <Skeleton w="34%" h={12} light />
          </div>
          <div className="cdh__panel">
            <Skeleton w={110} h={10} light />
            <Skeleton w="100%" h={42} light />
            <Skeleton w={130} h={18} light />
          </div>
        </section>
      </div>
    );
  }

  // A bookmarked id, or one that has dropped off the list.
  if (isError || !detail) {
    return (
      <div className="competition-detail">
        {crumb}
        <p className="cd-missing">
          {t('We could not load that competition.')}{' '}
          <Link to="/competition/all" className="cd-missing__link">
            {t('Back to all competitions')}
          </Link>
        </p>
      </div>
    );
  }

  const range = [fmtDate(detail.Start_date, 'dd MMM'), fmtDate(detail.End_Date, 'dd MMM yyyy')]
    .filter(Boolean)
    .join(' – ');

  return (
    <div className="competition-detail">
      {crumb}

      {/* ---- Hero, 1:15856 ---- */}
      <section className="cdh">
        <div className="cdh__main">
          <span className="cdh-status">
            <div className="cdh__tag">
              <span className={`cdh-status__dot is-${status}`} />
              {t(statusMeta.label)}
            </div>
          </span>

          <h1 className="cdh__title">{detail.competition_name}</h1>
          <p className="cdh__subtitle">
            {[
              detail.account_size_label &&
                t('{{size}} Challenge', { size: detail.account_size_label }),
              range,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {detail.description && <p className="cdh__description">{detail.description}</p>}

          <div className="cdh__tags">
            {tags.map((tag) => (
              <span className="cdh__tag" key={tag}>
                {tag}
              </span>
            ))}
          </div>
        </div>

        <div className="cdh__panel">
          <span className="cdh__panel-label">{upcoming ? t('Starts in') : t('Remaining time')}</span>
          <div className="cdh__units">
            {UNITS.map(([key, label]) => (
              <div className="cdh__unit" key={key}>
                <span className="cdh__unit-value">{pad(countdown[key])}</span>
                <span className="cdh__unit-label">{t(label)}</span>
              </div>
            ))}
          </div>

          <p className="cdh__prize">
            <span className="cdh__prize-amount">{usd(detail.prize_pool)}</span>
            <span className="cdh__prize-label">{t('prize pool')}</span>
          </p>

          {/* The hero's one action, always a way down the page — the panel is
              above the fold and its target is not. While the entry card is
              showing it goes to the form. Otherwise it goes to the leaderboard:
              the final standings once the competition has ended (N104), and
              for a trader who has entered, the table their own row is pinned
              to — which is what "Open competition" is there to show them. */}
          <Button
            variant="light"
            onClick={() => {
              const toEntry = canEnter && !joined;
              const target = toEntry
                ? document.getElementById('participate')
                : document.querySelector('.lb-table');
              target?.scrollIntoView({ behavior: 'smooth', block: toEntry ? 'center' : 'start' });
            }}
          >
            {status === 'ended'
              ? t('View results')
              : joined
                ? t('Open competition')
                : t('Enter competition')}
          </Button>
        </div>
      </section>

      {/* ---- Enter this competition — POST /v2/competition/participate/ ---- */}
      {canEnter && <ParticipateCard competitionId={competitionId} joined={joined} />}

      {/* ---- Leaderboard, 1:15856 ---- */}
      <LeaderboardTable
        rows={rows}
        currentUser={currentUser}
        count={board?.meta?.total}
        subtitle={
          status === 'ended'
            ? t('Final standings')
            : t('Live top {{count}} · updates as trades close', {
                count: board?.meta?.limit ?? rows.length,
              })
        }
      />

      {/* ---- Prize distribution + Competition rules, 1:15856 ---- */}
      <section className="cd-bottom">
        <div className="pd-card">
          <header className="pd-card__head">
            <span className="pd-card__title">
              <span className="icon pd-card__icon" style={icon('nav-competition')} />
              {t('Prize distribution')}
            </span>
            {/* Nothing to total when no places are published. */}
            {prizes.length > 0 && (
              <span className="pd-card__meta">
                {t('{{amount}} total', { amount: usd(detail.prize_pool) })}
              </span>
            )}
          </header>

          {rulesLoading && (
            <ul className="pd-list">
              {[0, 1, 2].map((row) => (
                <li className="pd-item" key={row}>
                  <Skeleton w={40} h={22} radius={999} />
                  <Skeleton w="42%" h={10} />
                </li>
              ))}
            </ul>
          )}

          {!rulesLoading && prizes.length > 0 && (
            <ul className="pd-list">
              {prizes.map((tier) => {
                // A range only reads as first place when it is rank 1 alone —
                // a "1–3" tier sharing one prize is not the winner's row.
                const from = tier.rank_from;
                const to = tier.rank_to ?? from;
                const alone = from === to;
                return (
                  <li className="pd-item" key={`${from}-${to}`}>
                    <span className={`pd-rank${alone && from === 1 ? ' is-first' : ''}`}>
                      {alone ? ordinal(from) : `${from}–${to}`}
                    </span>
                    <div className="pd-item__body">
                      <span className="pd-item__name">{tier.title || tier.prize}</span>
                      {/* The badge already says which ranks, so the line under
                          it carries what `rank_label` would only repeat: the
                          assessment actually awarded, and how many of it. */}
                      <span className="pd-item__desc">
                        {[
                          tier.challenge,
                          tier.count > 1 ? t('{{count}} winners', { count: tier.count }) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ') || tier.rank_label}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {!rulesLoading && prizes.length === 0 && (
            <p className="pd-empty">
              {t('No prize distribution published for this competition yet.')}
            </p>
          )}
        </div>

        <div className="rc-card">
          <header className="rc-card__head">
            <span className="rc-card__title">
              <span className="icon rc-card__icon" style={icon('table-rows')} />
              {t('Competition rules')}
            </span>
            <span className="rc-card__meta">{t('Enforced automatically')}</span>
          </header>

          {ruleText.length ? (
            <ol className="rc-list rc-list--numbered">
              {ruleText.map((rule, i) => (
                // The rules have no ids and the text is what identifies them.
                <li className="rc-rule" key={rule}>
                  <span className="rc-rule__n">{i + 1}</span>
                  {/* Rule 2 arrives with newlines in it — its lot limits are a
                      list inside one string, so they are kept as written. */}
                  <span className="rc-rule__text">{stripRulePrefix(rule)}</span>
                </li>
              ))}
            </ol>
          ) : (
            <ul className="rc-list">
              {ruleFacts.map((rule) => (
                <li className="rc-item" key={rule.label}>
                  <span className="rc-item__label">{t(rule.label)}</span>
                  <span className="rc-item__value">{rule.value}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
